"use client"

import { useState, useEffect, useRef } from "react"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import "./MapView.css"
import { Navigation } from "lucide-react"
import "./PlanningMapView.css"


// Planning-focused map component that reuses the main MapView logic
const PlanningMapView = ({ places, onBoundsChange, onPlanPlaces, selectedDate, placeTypes = {} }) => {
  console.log("🗺️ PlanningMapView rendered with:", {
    placesCount: places.length,
    placeTypesCount: Object.keys(placeTypes).length,
    selectedDate: selectedDate,
  })

  const mapRef = useRef(null)
  const markersRef = useRef({})
  const currentLocationMarkerRef = useRef(null)
  const mapContainerRef = useRef(null)
  const [visiblePlaces, setVisiblePlaces] = useState(places)
  const [visibleCount, setVisibleCount] = useState(places.length)
  const [isLocating, setIsLocating] = useState(false)
  const [currentLocation, setCurrentLocation] = useState(null)
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const [isShowingUserLocation, setIsShowingUserLocation] = useState(false)

  useEffect(() => {
    if (isSafari) {
      // Completely override Leaflet's default marker for Safari
      delete L.Icon.Default.prototype._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: `/images/default.png?safari=${Date.now()}&r=${Math.random()}`,
        iconUrl: `/images/default.png?safari=${Date.now()}&r=${Math.random()}`,
        shadowUrl: null,
        iconSize: [41, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [0, 0],
      })
    } else {
      // Standard setup for other browsers
      delete L.Icon.Default.prototype._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "/images/default.png",
        iconUrl: "/images/default.png",
        shadowUrl: null,
        iconSize: [41, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [0, 0],
      })
    }
  }, [])

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    // Create map with simple settings
    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      attributionControl: true,
    })

    // Add tile layer
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map)

    mapRef.current = map

    // Add moveend event listener for filtering
    map.on("moveend", () => {
      handleMapMove()
    })

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  // Add markers and fit bounds when places change
  useEffect(() => {
    console.log("🗺️ PlanningMapView received places:", {
      placesCount: places.length,
      mapExists: !!mapRef.current,
    })

    if (!mapRef.current || !places.length) return

    // Clear existing markers
    const existingMarkerCount = Object.keys(markersRef.current).length
    Object.values(markersRef.current).forEach((marker) => {
      mapRef.current?.removeLayer(marker)
    })
    markersRef.current = {}
    console.log(`🗑️ Cleared ${existingMarkerCount} existing markers`)

    // Add new markers for the places
    places.forEach((place, index) => {
      console.log(`📍 Creating marker ${index + 1}/${places.length} for: ${place.name}`)
      const marker = createMarkerForPlace(place)
      marker.addTo(mapRef.current)
      markersRef.current[place.id] = marker
    })

    console.log(`✅ Created ${places.length} new markers`)

    // Always fit map to show all markers on load/refresh
    if (places.length > 0) {
      const bounds = L.latLngBounds(places.map((place) => [place.lat, place.lng]))
      mapRef.current.fitBounds(bounds, { padding: [20, 20] })
      console.log("🎯 Fitted map bounds to show all places")
    }

    // Initial filter
    handleMapMove()
  }, [places])

  // Update markers when place types change
  useEffect(() => {
    console.log("🔄 Place types changed, updating markers:", placeTypes)

    Object.entries(markersRef.current).forEach(([placeId, marker]) => {
      const place = places.find((p) => p.id === placeId)
      if (place) {
        console.log(`🔄 Updating marker for place: ${place.name}`)
        updateMarkerAppearance(marker, place)
      } else {
        console.warn(`⚠️ Could not find place with ID: ${placeId}`)
      }
    })
  }, [placeTypes, places])

  // Handle current location functionality
  const handleLocationClick = () => {
    if (isShowingUserLocation) {
      // Switch back to showing all places
      if (mapRef.current && places.length > 0) {
        const bounds = L.latLngBounds(places.map((place) => [place.lat, place.lng]))
        mapRef.current.fitBounds(bounds, { padding: [20, 20] })
        setIsShowingUserLocation(false)
      }
    } else {
      // Show user location
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by this browser.")
        return
      }

      setIsLocating(true)

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const userLocation = [latitude, longitude]

          setCurrentLocation({ lat: latitude, lng: longitude })
          setIsLocating(false)
          setIsShowingUserLocation(true)

          if (mapRef.current) {
            // Remove existing current location marker if it exists
            if (currentLocationMarkerRef.current) {
              mapRef.current.removeLayer(currentLocationMarkerRef.current)
            }

            // Create a custom icon for current location
            const currentLocationIcon = L.divIcon({
              html: `
                <div class="current-location-marker">
                  <div class="location-pulse"></div>
                  <div class="location-dot"></div>
                </div>
              `,
              className: "current-location-icon",
              iconSize: [20, 20],
              iconAnchor: [10, 10],
            })

            // Add current location marker
            currentLocationMarkerRef.current = L.marker(userLocation, {
              icon: currentLocationIcon,
            })
              .addTo(mapRef.current)
              .bindPopup("📍 Your current location")

            // Zoom to current location
            mapRef.current.setView(userLocation, 16, {
              animate: true,
              duration: 1,
            })
          }
        },
        (error) => {
          setIsLocating(false)
          let errorMessage = "Unable to get your location. "

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += "Location access was denied. Please enable location permissions and try again."
              break
            case error.POSITION_UNAVAILABLE:
              errorMessage += "Location information is unavailable."
              break
            case error.TIMEOUT:
              errorMessage += "Location request timed out."
              break
            default:
              errorMessage += "An unknown error occurred."
              break
          }

          alert(errorMessage)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        },
      )
    }
  }

  const createMarkerForPlace = (place) => {
    const placeKey = `${place.lat},${place.lng}`
    const placeType = placeTypes[placeKey]

    console.log(`🏷️ Creating marker for "${place.name}":`, {
      placeKey,
      placeType,
      isLocalhost: window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1",
    })

    let icon
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"

    // Use the same icon logic as the original MapView
    if (isLocalhost || !placeType) {
      console.log(`📍 Using default icon for ${place.name} (localhost: ${isLocalhost}, placeType: ${placeType})`)
      icon = new L.Icon.Default()
    } else {
      // Use custom icons for production
      const cacheParam = isSafari ? `?safari=${Date.now()}&r=${Math.random()}` : `?t=${Date.now()}`

      if (placeType === "restaurant") {
        console.log(`🍽️ Using restaurant icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/restaurant.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "activity") {
        console.log(`🎯 Using activity icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/attraction.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "lodging") {
        console.log(`🏨 Using lodging icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/accommodation.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "nature") {
        console.log(`🌿 Using nature icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/nature.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "airport") {
        console.log(`✈️ Using airport icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/airport.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "coffee") {
        console.log(`☕ Using coffee icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/coffee.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "luggage") {
        console.log(`🧳 Using luggage icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/luggage.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "drinks") {
        console.log(`🍹 Using drinks icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/drinks.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "shopping") {
        console.log(`🛍️ Using shopping icon for ${place.name}`)
        icon = new L.Icon({
          iconUrl: `/images/shopping.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else {
        console.log(`❓ Unknown place type "${placeType}" for ${place.name}, using default icon`)
        icon = new L.Icon.Default()
      }
    }

    // Create the main marker
    const marker = L.marker([place.lat, place.lng], {
      icon,
    })

    console.log(`✅ Created marker for ${place.name} with icon:`, icon.options?.iconUrl || "default")

    // Add popup with place information - PLANNING VERSION
    const popupContent = createPlanningPopupContent(place)
    marker.bindPopup(popupContent)

    return marker
  }

  const createPlanningPopupContent = (place) => {
    return `
      <div class="place-popup">
        <h3>${place.name}</h3>
        <div class="popup-actions">
          <button onclick="window.planningMapPlanPlace && window.planningMapPlanPlace('${place.id}')" 
                  class="popup-btn" 
                  ${!selectedDate ? 'disabled style="opacity: 0.5; cursor: not-allowed;"' : ""}>
            📅 Plan This Place
          </button>
          ${!selectedDate ? '<p style="font-size: 12px; color: #666; margin: 4px 0 0 0;">Select a date first</p>' : ""}
        </div>
      </div>
    `
  }

  const updateMarkerAppearance = (marker, place) => {
    const placeKey = `${place.lat},${place.lng}`
    const placeType = placeTypes[placeKey]

    console.log(`🔄 Updating marker appearance for "${place.name}":`, {
      placeKey,
      placeType,
    })

    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"

    // Same icon logic as createMarkerForPlace
    let baseIcon
    if (isLocalhost || !placeType) {
      console.log(`📍 Updating to default icon for ${place.name}`)
      baseIcon = new L.Icon.Default()
    } else {
      // Use custom icons for production
      const cacheParam = isSafari ? `?safari=${Date.now()}&r=${Math.random()}` : `?t=${Date.now()}`

      if (placeType === "restaurant") {
        console.log(`🍽️ Updating to restaurant icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/restaurant.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "activity") {
        console.log(`🎯 Updating to activity icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/attraction.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "lodging") {
        console.log(`🏨 Updating to lodging icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/accommodation.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "nature") {
        console.log(`🌿 Updating to nature icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/nature.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "airport") {
        console.log(`✈️ Updating to airport icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/airport.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "coffee") {
        console.log(`☕ Updating to coffee icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/coffee.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "luggage") {
        console.log(`🧳 Updating to luggage icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/luggage.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "drinks") {
        console.log(`🍹 Updating to drinks icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/drinks.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else if (placeType === "shopping") {
        console.log(`🛍️ Updating to shopping icon for ${place.name}`)
        baseIcon = new L.Icon({
          iconUrl: `/images/shopping.png${cacheParam}`,
          iconSize: [41, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: null,
          shadowSize: [0, 0],
        })
      } else {
        console.log(`❓ Unknown place type "${placeType}" for ${place.name}, using default icon`)
        baseIcon = new L.Icon.Default()
      }
    }

    // Just use the base icon (no vote badges in planning view)
    marker.setIcon(baseIcon)

    // Update popup content
    const popupContent = createPlanningPopupContent(place)
    marker.setPopupContent(popupContent)
  }

  const handleMapMove = () => {
    if (!mapRef.current) return

    const bounds = mapRef.current.getBounds()
    const filtered = places.filter((place) => bounds.contains([place.lat, place.lng]))

    console.log(`🗺️ Planning map move detected: ${filtered.length}/${places.length} places visible in bounds`)

    setVisiblePlaces(filtered)
    setVisibleCount(filtered.length)

    // Notify parent component
    if (onBoundsChange) {
      onBoundsChange(filtered)
    }
  }

  const handlePlanVisiblePlaces = () => {
    if (visiblePlaces.length === 0) {
      alert("No places visible in current map area. Please zoom out or pan to include some locations.")
      return
    }

    if (!selectedDate) {
      alert("Please select a date first.")
      return
    }

    onPlanPlaces(visiblePlaces, selectedDate)
  }

  const handlePlanSinglePlace = (placeId) => {
    const place = places.find((p) => p.id === placeId)
    if (place && onPlanPlaces && selectedDate) {
      onPlanPlaces([place], selectedDate)
    }
  }

  // Make the function available globally for the popup button
  useEffect(() => {
    window.planningMapPlanPlace = handlePlanSinglePlace
    return () => {
      delete window.planningMapPlanPlace
    }
  }, [places, onPlanPlaces, selectedDate])

  // Force update visible places when places array changes
  useEffect(() => {
    if (mapRef.current && places.length > 0) {
      // Small delay to ensure map is ready
      setTimeout(() => {
        handleMapMove()
      }, 50)
    }
  }, [places])

  return (
    <div className="map-view">
      <div className="map-container-wrapper">
        <div ref={mapContainerRef} className="leaflet-map" />

        {/* Floating map controls */}
        <div className="floating-controls-plan">
          {/* Location button */}
          <button
            onClick={handleLocationClick}
            disabled={isLocating}
            className={`floating-btn location-btn ${isShowingUserLocation ? "active" : ""}`}
            title={isShowingUserLocation ? "Show all places" : "My Location"}
          >
            <Navigation
              size={20}
              style={{ color: isShowingUserLocation ? "white" : "#666" }}
              className={isLocating ? "spinning" : ""}
            />
          </button>
        </div>

        {/* Planning button - replaces voting button */}
        <div className="planning-controls">
          <button
            onClick={handlePlanVisiblePlaces}
            className="start-voting-btn"
            disabled={visibleCount === 0 || !selectedDate}
            style={{
              background: visibleCount > 0 && selectedDate ? "#10b981" : "#9ca3af",
            }}
          >
            {selectedDate
              ? `📅 Plan These ${visibleCount} Location${visibleCount !== 1 ? "s" : ""}`
              : "Select a Date First"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PlanningMapView
